import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Building, Loader2, Upload } from "@/components/ds/icons";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { handleApiError } from "@/lib/api-error";
import { useUser } from "@/contexts/UserContext";
import { formatRut } from "@/lib/rut";
import { cn } from "@/lib/utils";

const MIN_SIZE = 512;
const MAX_SIZE = 5000;
const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];
const ACCEPT_ATTR = "image/png,image/jpeg,image/webp";

interface EmpresaIdentidadCardProps {
  /** URL actual del logo (o null si no hay). */
  logoUrl: string | null;
  /** Se llama con la URL nueva tras subir, o null tras quitar. */
  onChange: (logoUrl: string | null) => void;
  /** Razón social guardada — NO el valor del formulario: esta tarjeta muestra
   *  la identidad persistida, no lo que el usuario está tipeando abajo. */
  razonSocial: string;
  /** RUT canónico (sin puntos ni guión) guardado. */
  rut: string;
  /**
   * La empresa tiene al menos un mandante por encima, así que opera como
   * contratista. Cuando es false no se muestra badge: "sin mandantes" NO
   * implica que la empresa sea mandante de alguien.
   */
  esContratista: boolean;
  isLoading?: boolean;
}

/**
 * Valida el archivo en el cliente ANTES de subir: da feedback inmediato y
 * evita un round-trip inútil. El backend sigue siendo la autoridad (revalida
 * tipo y dimensiones), así que esto es solo UX. Devuelve el mensaje de error
 * o null si el archivo es válido.
 */
function validarImagen(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    if (!ACCEPTED.includes(file.type)) {
      resolve("El logo debe estar en formato PNG, JPG o WEBP.");
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth < MIN_SIZE || img.naturalHeight < MIN_SIZE) {
        resolve(`La imagen debe tener al menos ${MIN_SIZE}x${MIN_SIZE} píxeles.`);
      } else if (img.naturalWidth > MAX_SIZE || img.naturalHeight > MAX_SIZE) {
        resolve(`La imagen no puede superar ${MAX_SIZE}x${MAX_SIZE} píxeles.`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve("No se pudo leer la imagen. Intenta con otro archivo.");
    };
    img.src = url;
  });
}

/**
 * Cabecera de identidad de "Mi Empresa": quién es la empresa (logo, razón
 * social, RUT) y el único control que la cambia sin pasar por el formulario —
 * el logo, que se sube y se borra de inmediato contra su propio endpoint.
 *
 * Va en una fila y no en una tarjeta de barra lateral porque el logo no es un
 * dato más del formulario: es la marca que se imprime en cada PDF firmado, y
 * su subida no participa del "Guardar cambios" de abajo.
 */
export function EmpresaIdentidadCard({
  logoUrl,
  onChange,
  razonSocial,
  rut,
  esContratista,
  isLoading = false,
}: EmpresaIdentidadCardProps) {
  const { refreshUser } = useUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  // URL de logo que el navegador no pudo cargar (archivo faltante en el disco,
  // APP_URL mal configurada en el backend, red caída). Guardamos la URL y no un
  // booleano: al subir un logo nuevo la URL cambia (lleva sufijo aleatorio), así
  // el estado de error se limpia solo sin necesidad de un efecto.
  const [logoRoto, setLogoRoto] = useState<string | null>(null);

  const busy = uploading || removing;
  // Hay logo guardado pero no se puede mostrar. Es distinto de "no hay logo":
  // el mensaje debe decirlo, no fingir que la tarjeta está vacía.
  const logoFallido = logoUrl !== null && logoRoto === logoUrl;
  const logoVisible = logoUrl !== null && !logoFallido;

  const subir = async (file: File) => {
    const error = await validarImagen(file);
    if (error) {
      toast.error(error);
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await api.postForm<{ data: { logo_url: string | null } }>(
        "/me/empresa/logo",
        fd,
      );
      onChange(res.data.logo_url);
      // Refresca user.empresa para que el chip de empresa (EmpresaSwitcher)
      // muestre el logo nuevo sin recargar. Aislado: si el refresh falla, la
      // subida YA fue exitosa — no debe mostrar un toast de error de subida.
      try {
        await refreshUser();
      } catch {
        // Refresh no crítico; el logo ya se guardó y se ve en la tarjeta.
      }
      toast.success("Logo actualizado");
    } catch (err) {
      handleApiError(err, "No se pudo subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const quitar = async () => {
    setRemoving(true);
    try {
      await api.deleteJson("/me/empresa/logo");
      onChange(null);
      try {
        await refreshUser();
      } catch {
        // Refresh no crítico; el logo ya se eliminó.
      }
      toast.success("Logo eliminado");
    } catch (err) {
      handleApiError(err, "No se pudo eliminar el logo");
    } finally {
      setRemoving(false);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Permite re-seleccionar el mismo archivo tras un error (reset del input).
    e.target.value = "";
    if (file) void subir(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void subir(file);
  };

  const abrirSelector = () => {
    if (!busy) inputRef.current?.click();
  };

  return (
    <section className="bg-card flex flex-wrap items-center gap-4 rounded-2xl border px-[18px] py-4">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={onInputChange}
        disabled={busy}
      />

      <button
        type="button"
        onClick={abrirSelector}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        disabled={busy}
        aria-label={logoUrl ? "Cambiar logo" : "Subir logo"}
        className={cn(
          "bg-surface-muted border-border-strong flex size-[76px] shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed transition-colors",
          "hover:border-primary/60 hover:bg-primary-50",
          dragging && "border-primary bg-primary-50",
          busy && "pointer-events-none opacity-60",
        )}
      >
        {uploading ? (
          <Loader2 className="text-soft size-6 animate-fc-spin" />
        ) : logoVisible ? (
          <img
            src={logoUrl}
            alt={`Logo de ${razonSocial || "la empresa"}`}
            onError={() => setLogoRoto(logoUrl)}
            className="size-full object-contain p-1.5"
          />
        ) : (
          <Building className="text-icon-soft size-7" />
        )}
      </button>

      <div className="flex min-w-[200px] flex-1 flex-col gap-1">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-52" />
            <Skeleton className="h-3.5 w-28" />
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base leading-tight font-semibold tracking-[-0.015em]">
                {razonSocial || "Sin razón social"}
              </span>
              {esContratista && <Badge variant="brand">Contratista</Badge>}
            </div>
            <span className="fc-num text-meta text-xs">
              {rut ? formatRut(rut) : "Sin RUT"}
            </span>
          </>
        )}
        <span className="text-soft text-[11.5px] leading-[1.45]">
          {logoFallido
            ? "No se pudo cargar el logo guardado. Sube uno nuevo para reemplazarlo."
            : "El logo se imprime en la esquina de cada PDF firmado. PNG, JPG o WEBP, mínimo 512×512."}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button variant="outline" size="xs" onClick={abrirSelector} disabled={busy}>
          <Upload className="size-4" />
          {logoVisible ? "Cambiar logo" : "Subir logo"}
        </Button>
        {logoUrl !== null && (
          <Button variant="ghost" size="xs" onClick={quitar} disabled={busy}>
            {removing ? <Loader2 className="size-4 animate-fc-spin" /> : null}
            Quitar
          </Button>
        )}
      </div>
    </section>
  );
}
